import { CanvasEx } from '@/typings/canvas';
import type { ElementsGroup } from '@/typings/customilyElements/elementsGroup';
import type { ListElements } from '@/typings/customilyElements/layer';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import useGroupManager from '@/modules/layers/composables/useGroupManager';

interface HandleGroupStatusProps {
    canvas: CanvasEx;
}

interface GroupProcessor {
    canvas: CanvasEx;
    relevantGroups: Set<ElementsGroup>;
}

const processCanvasElement = ({ canvas, relevantGroups }: GroupProcessor, element: ListElements): void => {
    const canvasObject = canvas.getObjects().find((obj) => obj.uuid === element.element.uuid);

    if (!canvasObject?.groupId) return;

    const group = canvas.getElementsGroups().find((g) => g.id === canvasObject.groupId);

    if (group) {
        relevantGroups.add(group);
    }
};

const processGroupElement = ({ canvas, relevantGroups }: GroupProcessor, element: ListElements): void => {
    const group = canvas.getElementsGroups().find((g) => g.uuid === element.element.uuid);

    if (group) {
        relevantGroups.add(group);
    }
};

export const handleGroupStatus = ({ canvas }: HandleGroupStatusProps, treeItems: ListElements[]): void => {
    if (!canvas || !treeItems.length) return;
    const globalGroupManager = useGroupManager(canvas);
    const mgr = globalGroupManager.get();
    if (!mgr) return;

    const relevantGroups = new Set<ElementsGroup>();
    const processor = { canvas, relevantGroups };

    const hasDirectGroupSelection = treeItems.some((item) => item.type === ListElementType.Group);

    for (const item of treeItems) {
        if (item.type === ListElementType.CanvasElement) {
            processCanvasElement(processor, item);
        } else if (item.type === ListElementType.Group) {
            processGroupElement(processor, item);
        }
    }

    if (hasDirectGroupSelection && relevantGroups.size > 0) {
        // Layer panel group selection: show all children layouts at once.
        mgr.setVisibility(Array.from(relevantGroups), true, { skipNestedExclusivity: true });
        return;
    }

    // Canvas element selection: enforce mutual exclusivity.
    // Include layout ancestors so setVisibility properly hides sibling layouts.
    const groupsToSelect: ElementsGroup[] = Array.from(relevantGroups);
    const coveredLayoutParentIds = new Set<string | null | undefined>();

    // If the element's direct group is itself a layout, mark its family as covered
    // so the defaults loop doesn't also include a sibling layout for the same parent.
    for (const group of relevantGroups) {
        if (group.isLayout) {
            coveredLayoutParentIds.add(group.parentId);
        }
    }

    for (const group of relevantGroups) {
        const ancestors = mgr.getAncestors(group.id);
        for (const anc of ancestors) {
            if (anc.isLayout) {
                groupsToSelect.push(anc);
                coveredLayoutParentIds.add(anc.parentId);
            }
        }
    }

    // For layout families not covered by the selection (or when the element
    // has no group at all), include default layouts so they reset properly.
    const defaults = mgr.getDefaultVisibleLayouts();
    for (const d of defaults) {
        if (!coveredLayoutParentIds.has(d.parentId)) {
            groupsToSelect.push(d);
        }
    }

    if (groupsToSelect.length > 0) {
        mgr.setVisibility(groupsToSelect, true);
    }
};
